/**
 * **Ivy §9's UI constants — presentation thresholds, and nothing else.**
 *
 * §9 is LOCKED that *"no product number is a literal in code or copy"*, and it
 * draws the line precisely: every number a player can **read** comes from config
 * (grid size, word count, the round timer, points per claim, the entry fee), and
 * the numbers below are **not** those. They are thresholds about how a screen
 * behaves — how long a message sits, how long a hold lasts, when a tap stops
 * being a tap — and §9's own table says they live *"in the client's design
 * constants: **not** in `platform_settings`, not in the DB, not in copy, not
 * inline at a call site"*.
 *
 * The one that is derived rather than fixed is `revealHoldMs`, and that is the
 * point of it: it is capped so it can never eat a configured `between_rounds_ms`.
 */

/** §5.7 — how long a `pending` capsule waits for the authoritative patch. */
export const CLAIM_PENDING_TIMEOUT_MS = 2500;

/** §5.5 — a `pointerdown`+`pointerup` inside these bounds is a tap, not a drag. */
export const TAP_COMMIT_MS = 250;
export const TAP_SLOP_PX = 10;

/** §5.5 — a forgotten anchor clears itself, so a later tap is never a surprise claim. */
export const ANCHOR_TIMEOUT_MS = 4000;

/**
 * §5.2 — below this cell size the hairline separators start to eat the glyph, so
 * the letter takes the larger of the two ratios.
 */
export const CELL_LEGIBILITY_FLOOR_PX = 20;
export const CELL_FONT_RATIO_ABOVE_FLOOR = 0.5;
export const CELL_FONT_RATIO_BELOW_FLOOR = 0.58;

/** §5.6 — Band B's dwell. The `+N` credit is the short one. */
export const FEEDBACK_SLOT_MS = 1600;
export const FEEDBACK_SLOT_SHORT_MS = 1200;

/** §14.3 part 3 — the in-flight window before the coin chip dims (Q10). */
export const BALANCE_RECONCILE_DIM_DELAY_MS = 400;

/** §9's cap on the reveal hold. Config can only ever shorten it. */
export const REVEAL_HOLD_CAP_MS = 2000;
const REVEAL_HOLD_FRACTION = 0.4;

/**
 * §5.9 — `revealHoldMs = min(2000, betweenRoundsMs × 0.4)`.
 *
 * `null` means the client cannot yet derive `between_rounds_ms` (the room has not
 * published a `nextRoundAt` to measure against). The cap is used in that window,
 * which is the safe direction: the derived value can only ever *shorten* the
 * hold, so an unknown one never lets it eat a configured between-rounds screen.
 */
export function revealHoldMs(betweenRoundsMs: number | null): number {
  if (betweenRoundsMs === null) return REVEAL_HOLD_CAP_MS;
  return Math.max(
    0,
    Math.min(REVEAL_HOLD_CAP_MS, Math.round(betweenRoundsMs * REVEAL_HOLD_FRACTION)),
  );
}

/**
 * §7 item 5 — the selection announcement is debounced so extending a drag does
 * not produce a stream, and §5.6's Band B region is throttled to one a second.
 */
export const SELECTION_ANNOUNCE_MS = 250;
export const FEEDBACK_ANNOUNCE_THROTTLE_MS = 1000;
